import { registerComponent } from "helpers";
import { WYATTElement } from "wyatt";

const suggestRequests = new Map();

function buildRequestUrl({ api, siteId, resultsPerPage, query, beacon }) {
  const params = new URLSearchParams({
    siteId,
    resultsFormat: "json",
    resultsPerPage,
    q: query,
    domain: window.location.href,
    pageLoadId: WYATT.searchspring.tracking.pageLoadId,
    sessionId: WYATT.searchspring.tracking.ssSessionIdNamespace,
    userId: WYATT.searchspring.tracking.ssUserId,
  });

  if (beacon) {
    params.set("beacon", "true");
  }

  return [
    "https://",
    `${siteId}.`,
    `${WYATT.config.searchspring.endpoints[api]}?`,
    params.toString(),
  ].join("");
}

// Every panel receives the same input event in one synchronous dispatch, so
// sharing the in-flight request keeps it to one suggest call per keystroke.
// Keyed on site and query: the resolving panels only read `suggested`, and the
// suggest panel subscribes first, so its request (with its resultsPerPage) is
// the one shared. It isn't tied to any panel's abort signal because other
// panels may still be waiting on it; each panel's sequence guard drops a
// stale response.
function fetchSuggestions({ siteId, query, resultsPerPage }) {
  const key = JSON.stringify([siteId, query]);

  if (!suggestRequests.has(key)) {
    suggestRequests.set(
      key,
      fetch(
        buildRequestUrl({ api: "suggest", siteId, resultsPerPage, query }),
        WYATT.config.searchspring.options.get,
      )
        .then((response) => response.json())
        .finally(() => suggestRequests.delete(key)),
    );
  }

  return suggestRequests.get(key);
}

// SearchSpring's suggest returns a misspelling as type "exact" with the
// corrected text ("strollr" gives "stroller") and a prefix as type
// "completed" ("strol" gives "stroller"), so the suggestion text is the term
// to search either way. Autocomplete alone spell-corrects prefixes to other
// words ("strol" to "stool").
function resolveQuery(suggestions, typed) {
  return suggestions?.suggested?.text || typed;
}

registerComponent(
  "predictive-search-results",
  class PredictiveSearchResults extends WYATTElement {
    constructor() {
      super();

      this.resultsContainer = this.querySelector("[data-results]");
      this.itemTemplate = this.querySelector('[data-template="item"]');
      this.abortController = null;
      this.requestSequence = 0;

      this.subscribe(
        WYATT.PubSub.events.searchInputChange,
        this.handleSearchInputChange.bind(this),
      );
    }

    // Beacon autocomplete events are product-typed, and only the products
    // sub-grid (not the collections/pages/articles/queries instances) carries
    // products on the dropdown.
    get isProductsAutocomplete() {
      return (
        this.config.api === "autocomplete" &&
        this.config.siteId === WYATT.config.searchspring.siteIds.products
      );
    }

    // Products and categories search the suggestion. Pages and articles keep
    // what was typed: their indexes match it better ("locat" finds Store
    // Locations on pages, while its suggestion "locks" finds Local Retail
    // Events).
    get resolvesQuery() {
      const { products, collections } = WYATT.config.searchspring.siteIds;

      return (
        this.config.api === "autocomplete" &&
        [products, collections].includes(this.config.siteId)
      );
    }

    requestUrl(query) {
      return buildRequestUrl({
        api: this.config.api,
        siteId: this.config.siteId,
        resultsPerPage: this.config.resultsPerPage,
        query,
        // Required for Searchspring to return a beacon-compatible responseId,
        // which the autocomplete beacon events reference. Only the products
        // dropdown emits beacon events, so scope it there.
        beacon: this.isProductsAutocomplete,
      });
    }

    handleSearchInputChange(event) {
      event.preventDefault();

      this.query = event.detail;

      this.fetchResults();
    }

    async fetchResults() {
      const render =
        this.config.api === "suggest"
          ? this.renderSuggestions.bind(this)
          : this.renderResults.bind(this);

      if (this.abortController) {
        this.abortController.abort();
      }

      this.abortController = new AbortController();
      this.requestSequence += 1;
      const requestId = this.requestSequence;

      try {
        let data;

        if (this.config.api === "suggest") {
          data = await fetchSuggestions({
            siteId: this.config.siteId,
            query: this.query,
            resultsPerPage: this.config.resultsPerPage,
          });
        } else {
          const typed = this.query;
          // Without a suggestion the panel searches what was typed, as before.
          const suggestions = this.resolvesQuery
            ? fetchSuggestions({
                siteId: WYATT.config.searchspring.siteIds.products,
                query: typed,
                resultsPerPage: this.config.resultsPerPage,
              }).catch(() => null)
            : null;
          // The redirect panel requests what was typed straight away and arms
          // the redirect from that response, as it always has, so the
          // redirect never waits on suggest. The suggestion only changes what
          // the panel shows.
          const typedResults =
            this.config.redirect || !suggestions
              ? this.fetchAutocomplete(typed)
              : null;

          if (this.config.redirect) {
            // A failure here is the typed request's; the render path below
            // awaits that same request when it shows the typed results.
            this.armRedirect(typedResults, requestId).catch(() => {});
          }

          const query = suggestions
            ? resolveQuery(await suggestions, typed)
            : typed;

          if (requestId !== this.requestSequence) return;

          data = await (query === typed && typedResults
            ? typedResults
            : this.fetchAutocomplete(query));
        }

        if (requestId !== this.requestSequence) return;

        this.dataset.ssResponseId = (data && data.responseId) || "";

        if (this.isProductsAutocomplete && WYATT.beacon) {
          WYATT.beacon.autocomplete.render({
            responseId: this.dataset.ssResponseId,
            results: (data.results || []).map(WYATT.beacon.normalizeResult),
          });
        }

        render(data);
      } catch (error) {
        if (error.name !== "AbortError") {
          throw error;
        }
      } finally {
        if (requestId === this.requestSequence) {
          this.abortController = null;
        }
      }
    }

    async fetchAutocomplete(query) {
      const response = await fetch(this.requestUrl(query), {
        ...WYATT.config.searchspring.options.get,
        signal: this.abortController.signal,
      });

      return response.json();
    }

    async armRedirect(typedResults, requestId) {
      const data = await typedResults;

      if (requestId !== this.requestSequence) return;

      this.publish(WYATT.PubSub.events.redirectFetched, data);
    }

    renderSuggestions({ alternatives, suggested }) {
      if (alternatives.length === 0 || !suggested) {
        this.resultsContainer.innerHTML = `<p>${WYATT.t.predictiveSearchResults.noResults}</p>`;
        return;
      }

      this.resultsContainer.innerHTML = "";

      const suggestedItem =
        this.itemTemplate.content.cloneNode(true).firstElementChild;

      suggestedItem.dataset.query = JSON.stringify(suggested);
      this.resultsContainer.appendChild(suggestedItem);

      alternatives.forEach((alternative) => {
        const alternativeItem =
          this.itemTemplate.content.cloneNode(true).firstElementChild;

        alternativeItem.dataset.query = JSON.stringify(alternative);
        this.resultsContainer.appendChild(alternativeItem);
      });
    }

    renderResults(data) {
      const { results } = data;

      if (this.impressionObserver) {
        this.impressionObserver.disconnect();
        this.impressionObserver = null;
      }

      if (this.config.brands) {
        this.publish(
          WYATT.PubSub.events.predictiveSearchBrandsFetched,
          data?.facets,
        );
      }

      if (results.length === 0) {
        this.resultsContainer.innerHTML = `<p>${WYATT.t.predictiveSearchResults.noResults}</p>`;
        return;
      }

      this.resultsContainer.innerHTML = "";

      results.forEach((result) => {
        const item =
          this.itemTemplate.content.cloneNode(true).firstElementChild;

        item.dataset.result = JSON.stringify(result);

        if (this.isProductsAutocomplete) {
          item.dataset.ssResult = JSON.stringify({
            uid: result.uid,
            parentId: result.parentId ?? result.uid,
            sku: result.sku,
          });
        }

        this.resultsContainer.appendChild(item);
      });

      if (this.isProductsAutocomplete && WYATT.beacon) {
        this.impressionObserver = WYATT.beacon.observeImpressions(
          this,
          "autocomplete",
        );
      }
    }
  },
);
